import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {makeGetReferencedRecipient} from '../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {
  AnnotationCommentHighlightModel,
  AnnotationMessageHighlightModel,
} from '../../../models/api/annotationModel';
import {AppState} from '../../../store/appState';
import {getActivities, getMessages} from '../../../store/data/dataSelectors';
import {getFindTeammate} from '../../../store/data/teammates/teammatesSelectors';
import {AnnotationSummary} from './annotationSummary';

/*
 * Props.
 */

interface AnnotationSummaryContainerProps {
  annotation: AnnotationMessageHighlightModel | AnnotationCommentHighlightModel;
  referencedActivityId: number;
}

/*
 * Selectors.
 */

function getReferencedActivityId(_state: AppState, props: AnnotationSummaryContainerProps) {
  return props.referencedActivityId;
}

function makeGetReferencedActivity() {
  return createSelector(
    [getReferencedActivityId, getActivities],
    (activityId, activities) => activities[activityId],
  );
}

function makeGetMessage(getReferencedActivity: ReturnType<typeof makeGetReferencedActivity>) {
  return createSelector([getReferencedActivity, getMessages], (referencedActivity, messages) => {
    if (!referencedActivity || referencedActivity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
      return undefined;
    }

    return messages[referencedActivity.messageUid];
  });
}

function makeGetAuthor(getMessage: ReturnType<typeof makeGetMessage>) {
  return createSelector([getMessage, getFindTeammate], (message, findTeammate) => {
    if (!message || !message.authorId) {
      return undefined;
    }

    return findTeammate(message.authorId);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AnnotationSummaryContainerProps>()(() => {
  const getReferencedActivity = makeGetReferencedActivity();
  const getMessage = makeGetMessage(getReferencedActivity);

  return {
    message: getMessage,
    author: makeGetAuthor(getMessage),
    referencedRecipient: makeGetReferencedRecipient(getReferencedActivity),
  };
});

export const AnnotationSummaryContainer = connect(mapStateToProps)(AnnotationSummary);
